import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'
import { StudentAnswer } from '../../pages/student/examPage/components/types'
import {
    PassedTestState,
    StudentTestResultState,
    TestQuestionState,
    TestState,
} from '../types'

interface Answer {
    content: string
    id: number
    position: number
    questionId: number
}
interface Exam {
    duration: number
    questions: Answer[]
    startedAt: string
    topicTestTitle: string
    topicTestId: number
    topicTestResultId: number
}

interface getStudentTestAnalysisParams {
    courseTopicId: number
    studentId: number
}

export const testApiSlice = createApi({
    reducerPath: 'testApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['Test', 'TestQuestions'],
    endpoints: (build) => ({
        createTest: build.mutation<void, TestState>({
            query: (topic) => ({
                url: 'topic-test',
                method: 'POST',
                body: topic,
            }),
            invalidatesTags: [{ type: 'Test', id: 'LIST' }],
        }),

        updateTest: build.mutation<void, TestState>({
            query: (test) => ({
                url: `topic-test/${test.id}`,
                method: 'PUT',
                body: test,
            }),
            invalidatesTags: [{ type: 'Test', id: 'LIST' }],
        }),

        deleteTest: build.mutation<{ success: boolean; id: number }, number>({
            query: (id) => ({
                url: `/topic-test/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Test', id }],
        }),

        createTestQuestions: build.mutation<void, TestQuestionState>({
            query: (testQuestions) => ({
                url: 'question',
                method: 'POST',
                body: testQuestions,
            }),
            invalidatesTags: ['TestQuestions'],
        }),

        getTestQuestions: build.query<any, number>({
            query: (topicTestId) => ({
                url: 'question',
                method: 'get',
                params: {
                    topicTestId: topicTestId,
                },
            }),
            providesTags: ['TestQuestions'],
            transformResponse: (response: any) => response.data,
        }),

        getTestResultAnalysis: build.query<any, number>({
            query: (topicTestId) => ({
                url: `topic-test-result/analysis/${topicTestId}`,
            }),
            transformResponse: (response: any) => response.data,
        }),

        getStudentTestResult: build.query<StudentTestResultState, number>({
            query: (topicTestId) => ({
                url: `topic-test-result/review/${topicTestId}`,
            }),
            transformResponse: (response: any) => response.data,
        }),

        fetchAllExamQuestions: build.query<
            Exam,
            {
                topicTestId: string
                userTopicEnrollId: string
                topicTestResultId: string
                sessionId: number
            }
        >({
            query: ({
                topicTestId,
                userTopicEnrollId,
                topicTestResultId,
                sessionId,
            }) => ({
                url: `/topic-test-result/start`,
                method: 'POST',
                body: {
                    topicTestId,
                    userTopicEnrollId,
                    topicTestResultId,
                    sessionId,
                },
            }),
            transformResponse: (response: any) => response.data,
        }),

        finishExam: build.mutation<
            void,
            { answers: StudentAnswer[]; topicTestResultId: number }
        >({
            query: ({ answers, topicTestResultId }) => ({
                url: `/topic-test-result/finish/${topicTestResultId}`,
                method: 'POST',
                body: { questions: answers },
            }),
        }),

        getStudentTestAnalysis: build.query<
            PassedTestState[],
            getStudentTestAnalysisParams
        >({
            query: ({ courseTopicId, studentId }) => ({
                url: `topic-test-result/student-analysis/course-topic/${courseTopicId}`,
                params: {
                    studentId,
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ topicTestId }) => ({
                              type: 'Test' as const,
                              topicTestId,
                          })),
                          { type: 'Test', id: 'LIST' },
                      ]
                    : [{ type: 'Test', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),
    }),
})
